import type { AeCampaign, AeCampaignListItem } from '@/types/assessmentEngine';

export type AeResponsePeriodMode = 'single_wave' | 'per_period';

function parseTimePeriodConfig(
  raw: AeCampaign['time_period_config'] | string | null | undefined
): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return null;
}

/** Whether respondents may submit once per time period (open campaign) vs one wave only. */
export function campaignResponsePeriodMode(
  campaign: Pick<AeCampaign, 'time_period_config' | 'campaign_type' | 'series_id'>
): AeResponsePeriodMode {
  const config = parseTimePeriodConfig(campaign.time_period_config);
  const granularity = config?.granularity;
  if (typeof granularity === 'string' && granularity.length > 0) {
    return 'per_period';
  }
  if (campaign.series_id) {
    return 'per_period';
  }
  if (campaign.campaign_type === 'evergreen' || campaign.campaign_type === 'recurring') {
    return 'per_period';
  }
  return 'single_wave';
}

export function campaignPeriodGranularity(
  config: AeCampaign['time_period_config'] | string | null | undefined
): string | null {
  const parsed = parseTimePeriodConfig(config);
  const g = parsed?.granularity;
  return typeof g === 'string' && g.length > 0 ? g : null;
}

/** Legacy recurring series without time_period_config on the campaign row. */
export function campaignIsLegacyRecurringSeries(
  campaign: Pick<AeCampaign, 'campaign_type' | 'series_id' | 'time_period_config'>
): boolean {
  return !!campaign.series_id && !campaignPeriodGranularity(campaign.time_period_config);
}

function readListNumber(row: Record<string, unknown>, snake: string, camel: string): number {
  const raw = row[snake] ?? row[camel];
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function normalizeCampaignListItem(row: AeCampaignListItem): AeCampaignListItem {
  const raw = row as AeCampaignListItem & Record<string, unknown>;
  const branding =
    row.branding && typeof row.branding === 'object'
      ? (row.branding as Record<string, unknown>)
      : null;
  const orgName =
    typeof branding?.organization_display_name === 'string'
      ? branding.organization_display_name.trim()
      : '';

  const administratorEmail =
    typeof raw.administrator_email === 'string' ? raw.administrator_email.trim() : '';

  return {
    ...row,
    time_period_config: parseTimePeriodConfig(row.time_period_config) ?? row.time_period_config,
    invite_count: readListNumber(raw, 'invite_count', 'inviteCount'),
    response_count: readListNumber(raw, 'response_count', 'responseCount'),
    complete_count: readListNumber(raw, 'complete_count', 'completeCount') || readListNumber(raw, 'response_count', 'responseCount'),
    company_name: row.company_name?.trim() || orgName || null,
    administrator_name:
      row.administrator_name?.trim() || administratorEmail || null,
  };
}

export function campaignResponseModeLabelKey(
  campaign: Pick<AeCampaign, 'time_period_config' | 'campaign_type' | 'series_id'>
): string {
  if (campaignIsLegacyRecurringSeries(campaign)) {
    return 'assessmentEngine.responseMode.recurring_series';
  }
  const mode = campaignResponsePeriodMode(campaign);
  const granularity = campaignPeriodGranularity(campaign.time_period_config);
  if (mode === 'per_period' && granularity) {
    return 'assessmentEngine.responseMode.perPeriodWithGranularity';
  }
  return `assessmentEngine.responseMode.${mode}`;
}
